import { fetchAddCollectList, fetchAddLikeList, fetchRemoveCollectList, fetchRemoveLikeList, fetchShortList } from 'api'
import { AdvertisementType } from 'constant/advertisement'
import { FALSE, TRUE } from 'constant/common'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectAdInfo } from 'redux/selector/app'
import { useImmer } from 'use-immer'
import PageWrapper from './components/PageWrapper'
import { PlayerControlProvider, usePlayerControl } from './components/PlayerPage'
import { VirtualPages } from './components/VirtualPage'
import { IconBack, Wrapper } from './Styled'
import useAlertWorksActionResult from 'hooks/useAlertWorksActionResult'
import { useMutex } from 'hooks/useMutex'
import { useSharedCache } from 'hooks/useDirtyWorkData'
import { useNavigateTo, useParamsOfPage } from 'hooks/useNavigateTo'
import { usePageActivated } from 'app-layered-layout/usePageActivated'
import { useAppNavigate } from 'app-layered-layout/useAppNavigate'
import { flushSync } from 'react-dom'
import { RESET_SHORT_FAVOURITE } from 'redux/constant/shortFavourite'
import { RESET_SHORT_COLLECTION } from 'redux/constant/shortCollection'
import { combinedStorage } from 'utils/combinedStorage'
import Tutorial from './components/Tutorial'

const AD_INTERVAL = 5
const TUTORIAL_SKIP_VARIABLE = 'viewedShortTutorialAt'

interface ViewShortProps {
  isBrowserPage?: boolean
}

function ViewShort({ isBrowserPage = false }: ViewShortProps) {
  const dispatch = useDispatch()

  const { navigateToViewShort, navigateToViewPartShort } = useNavigateTo()
  const navigate = useAppNavigate()
  const listFromUrl = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const listStr = params.get('list') || ''
      const arr = listStr
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      return arr.length ? arr : []
    } catch (e) {
      return []
    }
  }, [])
  const {
    videoInfo: videoInfoInitial,
    videoList: videoListInitial,
    currentIndex = 0,
    posterImg,
  } = useParamsOfPage('short')
  // 如果url有list參數，則使用list參數，否則使用videoListInitial
  const videoIdList = useMemo(() => {
    // 如果資料來自listFromUrl，實際上只會有id
    if (!isBrowserPage) return videoListInitial

    if (listFromUrl.length === 0) {
      return [videoInfoInitial]
    }
    return listFromUrl.map((m: string) => ({ id: m }))
  }, [isBrowserPage, listFromUrl, videoInfoInitial, videoListInitial])

  const pageActivated = usePageActivated()
  const updateIndex = (index: number) => {
    // 注意這邊要特別處理在瀏覽器開啟的邏輯
    if (isBrowserPage) {
      navigateToViewPartShort(
        {
          videoInfo: videoInfoInitial,
          videoList: videoIdList,
          currentIndex: index,
          posterImg,
        },
        { replace: true }
      )
    } else {
      navigateToViewShort(
        {
          videoInfo: videoInfoInitial,
          videoList: videoIdList,
          currentIndex: index,
          posterImg,
        },
        { replace: true }
      )
    }
  }

  const videoIndexInitial = videoIdList.findIndex((i: any) => i.id === videoInfoInitial.id)
  const { addDirtyData, useMappedData } = useSharedCache()
  const videoList = useMappedData('short', videoIdList)

  const adViewShort: any[] = useSelector(selectAdInfo)[AdvertisementType.ViewShort] ?? []

  const [imageCache, patchImageCache] = useImmer<Record<string, { preloaded: boolean; src: string | null }>>({
    [videoInfoInitial.id]: {
      preloaded: posterImg != null,
      src: posterImg as string | null,
    },
  })

  // tutorial
  const [skipTutorial] = useState(() => {
    return combinedStorage.getItem(TUTORIAL_SKIP_VARIABLE) === process.env.REACT_APP_VERSION
  })

  const setTutorialSkip = () => {
    combinedStorage.setItem(TUTORIAL_SKIP_VARIABLE, process.env.REACT_APP_VERSION as string)
  }

  const updateImageCache = (id: any, base64Image: string | null) => {
    if (!base64Image) return
    patchImageCache((arg) => {
      if (arg[id] == null) {
        arg[id] = {
          preloaded: false,
          src: base64Image,
        }
      }
    })
  }

  const commitImageCache = () => {
    patchImageCache((arg) => {
      for (const k of Object.keys(arg)) {
        if (arg[k].src) {
          arg[k].preloaded = true
        }
      }
    })
  }

  const mapIndex = (originalIndex: number, adCount: number): { type: 'short' | 'ad'; index: number } => {
    if (adCount === 0) {
      return {
        type: 'short',
        index: originalIndex % videoList.length,
      }
    }

    /**
     *  -7  -6  -5  -4  -3  -2  -1  +0 +1 +2 +3 +4 +5 +6  7  8  9 10  11
     * [a-2]s-5 s-4 s-3 s-2 s-1[a-1]s0 s1 s2 s3 s4[a0]s5 s6 s7 s8 s9 [a1]
     */

    const segmentLength = AD_INTERVAL + 1
    let isAd = false
    let adIndex = 0
    let shortIndex = 0
    if (originalIndex >= 0) {
      isAd = (originalIndex + 1) % segmentLength === 0
      shortIndex = originalIndex - ~~(originalIndex / segmentLength)
      adIndex = ~~(originalIndex / segmentLength)
    } else {
      isAd = -(originalIndex + 1) % segmentLength === 0
      shortIndex = originalIndex - Math.floor(originalIndex / segmentLength)
      adIndex = Math.floor(originalIndex / segmentLength)
    }

    if (isAd) {
      return {
        type: 'ad',
        index: adIndex,
      }
    } else {
      return {
        type: 'short',
        index: shortIndex,
      }
    }
  }
  const mod = (a: number, b: number) => ((a % b) + b) % b

  const selectPage = (originalIndex: number, baseOffset: number, shorts: any[], ads: any[]) => {
    const index = mapIndex(originalIndex, ads.length)
    if (index.type === 'short') {
      return {
        type: 'short' as const,
        item: shorts[mod(index.index + baseOffset, videoList.length)],
      }
    } else {
      return {
        type: 'ad' as const,
        item: ads[mod(index.index, adViewShort.length)],
      }
    }
  }

  // a proper mod that handles negative value

  const [videoBase] = useState(videoIndexInitial)

  // DOM 重排：slotOrder 決定 instance 在 [prev, current, next] 的順序，transition 時只更新被回收的 instance
  // onNext: [1,2,0] 原 next→current、current→prev、prev 回收給新 next
  // onPrev: [2,0,1] 原 prev→current、current→next、next 回收給新 prev
  const lastTransitionIndexRef = useRef<number | null>(null)
  const [slotOrder, setSlotOrder] = useState([0, 1, 2])

  // displayIndex: transition 時導航可能尚未完成，currentIndex 仍為舊值，需用 lastTransitionIndexRef 取得正確 index
  const displayIndex = lastTransitionIndexRef.current ?? currentIndex
  const prevPage = selectPage(displayIndex - 1, videoBase, videoList, adViewShort)
  const currentPage = selectPage(displayIndex, videoBase, videoList, adViewShort)
  const nextPage = selectPage(displayIndex + 1, videoBase, videoList, adViewShort)

  // 註冊以 video ID 為 key 的影片實例控制器
  const { pause, currentTime, remove, setAutoplay: setAutoplayCurrent } = usePlayerControl(currentPage.item.id)
  const { play: playNext } = usePlayerControl(nextPage.item.id)
  const { play: playPrev } = usePlayerControl(prevPage.item.id)

  useEffect(() => {
    if (!pageActivated) {
      return
    }

    if (currentPage.type !== 'short') {
      return
    }

    const controller = new AbortController()
    const signal = controller.signal
    async function run() {
      const id = currentPage.item.id
      const newShortData = (await fetchShortList({ signal, id })).data
      addDirtyData('short', newShortData)
    }

    run().catch((err: any) => {})

    return () => {
      controller.abort()
    }
  }, [addDirtyData, currentPage.item.id, currentPage.type, pageActivated])

  const MIN_STAY_TIME = 500

  useEffect(() => {
    if (currentPage.type === 'ad') {
      return
    }

    const id = currentPage.item.id

    let waitingForLeave = false

    const waitId = setTimeout(() => {
      waitingForLeave = true
    }, MIN_STAY_TIME)

    return () => {
      clearTimeout(waitId)
      if (waitingForLeave) {
        fetchShortList({ id })
          .then((response) => {
            addDirtyData('short', response.data)
          })
          .catch(() => {})
      }
    }
  }, [addDirtyData, currentPage.item.id, currentPage.type])

  const virtualPages = useRef<any>()

  const alerts = useAlertWorksActionResult()

  const { wrap: wrapAddLike } = useMutex()
  const handleAddLikeList = wrapAddLike(async (video: any) => {
    if (video.like === TRUE) {
      // WARN: force cast string to number because js do not support int64.
      await fetchRemoveLikeList({ video_id: video?.id as unknown as number })
    } else {
      await fetchAddLikeList({ video_id: video?.id })
    }

    const oldShortData = video

    let newState =
      oldShortData.like === TRUE
        ? {
            like: FALSE,
            like_count: Math.max(oldShortData.like_count - 1, 0),
          }
        : {
            like: TRUE,
            like_count: oldShortData.like_count + 1,
          }

    const newShortData = {
      ...oldShortData,
      ...newState,
    }

    addDirtyData('short', [newShortData])

    dispatch({ type: RESET_SHORT_FAVOURITE })
    dispatch({ type: RESET_SHORT_COLLECTION })
  })

  const { wrap: wrapAddCollection } = useMutex()
  const handleAddCollectList = wrapAddCollection(async (video: any) => {
    if (video.favorite === TRUE) {
      // WARN: force cast string to number because js do not support int64.
      await fetchRemoveCollectList({ video_id: video?.id as unknown as number })
    } else {
      await fetchAddCollectList({ video_id: video?.id })
    }

    const oldShortData = video

    let newState =
      oldShortData.favorite === TRUE
        ? {
            favorite: FALSE,
            favorite_count: Math.max(oldShortData.favorite_count - 1, 0),
          }
        : {
            favorite: TRUE,
            favorite_count: oldShortData.favorite_count + 1,
          }

    const newShortData = {
      ...oldShortData,
      ...newState,
    }

    addDirtyData('short', [newShortData])

    dispatch({ type: RESET_SHORT_FAVOURITE })
    dispatch({ type: RESET_SHORT_COLLECTION })

    if (video.favorite === FALSE) {
      alerts.alertSaved()
    }
  })

  const preloadedImages = [
    ...(prevPage?.type === 'short' && imageCache[prevPage?.item?.id]?.src
      ? [
          {
            key: prevPage.item.coverphoto_v,
            value: imageCache[prevPage.item.id].src,
          },
        ]
      : []),
    ...(nextPage?.type === 'short' && imageCache[nextPage?.item?.id]?.src
      ? [
          {
            key: nextPage.item.coverphoto_v,
            value: imageCache[nextPage.item.id].src,
          },
        ]
      : []),
  ]

  const onNext = () => {
    flushSync(() => {
      // player 在實例建立時已設定 autoplay，在輪播重複使用同個實例時，所以需要手動改寫 autoplay 狀態
      setAutoplayCurrent(false)
      // 移除控制器的暫存
      remove(prevPage.item.id)
      // 暫停前一部影片的播放與重置時間軸
      pause()
      currentTime(0)
      // 播放下一部影片
      playNext()
      // 更新 index 並設定 transition 狀態
      const newIndex = currentIndex + 1
      lastTransitionIndexRef.current = newIndex
      setSlotOrder((s) => [s[1], s[2], s[0]])
      updateIndex(newIndex)
      commitImageCache()
      virtualPages.current.resetPageOffset()
    })
  }

  const onPrev = () => {
    flushSync(() => {
      // player 在實例建立時已設定 autoplay，在輪播重複使用同個實例時，所以需要手動改寫 autoplay 狀態
      setAutoplayCurrent(false)
      // 移除控制器的暫存
      remove(nextPage.item.id)
      // 暫停前一部影片的播放與重置時間軸
      pause()
      currentTime(0)
      // 播放前一部影片
      playPrev()
      // 更新 index 並設定 transition 狀態
      const newIndex = currentIndex - 1
      lastTransitionIndexRef.current = newIndex
      setSlotOrder((s) => [s[2], s[0], s[1]])
      updateIndex(newIndex)
      commitImageCache()
      virtualPages.current.resetPageOffset()
    })
  }

  // targetPage: 'prev' | 'next' 表示滑動超過 50% 時要前往的目標頁
  const onScrollPast50 = (targetPage: 'prev' | 'next') => {
    pause()
    if (targetPage === 'prev') {
      playPrev()
    } else {
      playNext()
    }
  }

  return (
    <Wrapper>
      <VirtualPages ref={virtualPages} onNext={onNext} onPrev={onPrev} onScrollPast50={onScrollPast50}>
        <PageWrapper
          key={`instance-${slotOrder[0]}`}
          pageActivated={pageActivated}
          imageCache={imageCache}
          updateImageCache={updateImageCache}
          preloadedImages={preloadedImages}
          autoplay={false}
          handleAddLikeList={handleAddLikeList}
          handleAddCollectList={handleAddCollectList}
          videoInfo={prevPage.item}
          type={prevPage.type}
        />
        <PageWrapper
          key={`instance-${slotOrder[1]}`}
          pageActivated={pageActivated}
          imageCache={imageCache}
          updateImageCache={updateImageCache}
          preloadedImages={preloadedImages}
          autoplay={true}
          handleAddLikeList={handleAddLikeList}
          handleAddCollectList={handleAddCollectList}
          videoInfo={currentPage.item}
          type={currentPage.type}
        />
        <PageWrapper
          key={`instance-${slotOrder[2]}`}
          pageActivated={pageActivated}
          imageCache={imageCache}
          updateImageCache={updateImageCache}
          preloadedImages={preloadedImages}
          autoplay={false}
          handleAddLikeList={handleAddLikeList}
          handleAddCollectList={handleAddCollectList}
          videoInfo={nextPage.item}
          type={nextPage.type}
        />
      </VirtualPages>
      {!isBrowserPage && (
        <IconBack
          onClick={() => {
            navigate(-1)
          }}
        />
      )}
      <Tutorial isOpen={!skipTutorial} onFinished={setTutorialSkip} />
    </Wrapper>
  )
}

export default function ViewShortWrapper({ isBrowserPage = false }: ViewShortProps) {
  return (
    <PlayerControlProvider>
      <ViewShort isBrowserPage={isBrowserPage} />
    </PlayerControlProvider>
  )
}
